import { useQuery } from '@tanstack/react-query';

import {
  getHttpResponseStatus,
  shouldRetryUnlessHttpServerError
} from '@/adapters/query';
import { dataCenterClient } from '@/adapters/schemaClients';
import { DataCenterNavigation } from '@/types/data-center';
import { FindDataCenterNavigationFacilityTypeEnum } from '@/types/data-center/apis/DefaultApi';

export const DATA_CENTER_V3_QUERY_KEY = 'useDataCenterV3QueryKey';

const NAVIGATION_STALE_TIME_MS = 5 * 60 * 1000;

export type DataCenterFacilityType = FindDataCenterNavigationFacilityTypeEnum;

export type OmniIFrameRequest =
  | { kind: 'home'; facilityType: DataCenterFacilityType }
  | {
      kind: 'dashboard';
      dashboardId: string;
      facilityType: DataCenterFacilityType;
      openAskAi: boolean;
    }
  | { kind: 'ask-ai' };

export const useDataCenterNavigationQuery = (
  facilityType: DataCenterFacilityType | undefined
) =>
  useQuery<DataCenterNavigation, Error>(
    [DATA_CENTER_V3_QUERY_KEY, 'navigation', facilityType],
    async () => {
      const response = await dataCenterClient.findDataCenterNavigation({
        facilityType: facilityType!
      });
      return response.data ?? { home_dashboard_id: null, categories: [] };
    },
    {
      enabled: !!facilityType,
      staleTime: NAVIGATION_STALE_TIME_MS,
      retry: (failureCount, error) =>
        getHttpResponseStatus(error) === undefined &&
        shouldRetryUnlessHttpServerError(failureCount, error)
    }
  );

const fetchOmniIFrameUrl = async (
  request: OmniIFrameRequest
): Promise<string> => {
  const user_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await (() => {
    switch (request.kind) {
      case 'home':
        return dataCenterClient.omniGenerateHomeIFrameUrl({
          omniGenerateHomeIFrameUrlRequest: {
            facility_type: request.facilityType,
            user_timezone
          }
        });
      case 'dashboard':
        return dataCenterClient.omniGenerateDashboardIFrameUrl({
          omniGenerateDashboardIFrameUrlRequest: {
            dashboard_id: request.dashboardId,
            facility_type: request.facilityType,
            open_ask_ai: request.openAskAi,
            user_timezone
          }
        });
      case 'ask-ai':
        return dataCenterClient.omniGenerateAskAiIFrameUrl({
          omniGenerateIFrameUrlForReportBuilderRequest: { user_timezone }
        });
    }
  })();
  const iFrameUrl = response.data?.iframe_url;
  if (!iFrameUrl) {
    throw new Error('Omni iframe url missing from response');
  }
  return iFrameUrl;
};

// Omni signs each embed url per request, so every mount asks for a fresh one
// and nothing is cached once the embed unmounts.
export const useOmniIFrameUrlQuery = (
  request: OmniIFrameRequest,
  onError: () => void
) =>
  useQuery<string, Error>(
    [DATA_CENTER_V3_QUERY_KEY, 'iframe', request],
    () => fetchOmniIFrameUrl(request),
    { cacheTime: 0, staleTime: Infinity, retry: false, onError }
  );
